import { useState } from "react";
import { Search, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

export interface ProductFiltersState {
  search: string;
  category: string;
  tier: string;
  status: string;
  minPrice: number;
  maxPrice: number;
}

interface ProductFiltersProps {
  filters: ProductFiltersState;
  onFiltersChange: (filters: ProductFiltersState) => void;
  categories: string[];
  tiers: string[];
  maxPriceLimit: number;
}

export function ProductFilters({
  filters,
  onFiltersChange,
  categories,
  tiers,
  maxPriceLimit,
}: ProductFiltersProps) {
  const [priceRange, setPriceRange] = useState<[number, number]>([
    filters.minPrice,
    filters.maxPrice || maxPriceLimit,
  ]);

  const activeFiltersCount = [
    filters.category !== "all",
    filters.tier !== "all",
    filters.status !== "all",
    filters.minPrice > 0 || (filters.maxPrice > 0 && filters.maxPrice < maxPriceLimit),
  ].filter(Boolean).length;

  const clearFilters = () => {
    setPriceRange([0, maxPriceLimit]);
    onFiltersChange({
      search: "",
      category: "all",
      tier: "all",
      status: "all",
      minPrice: 0,
      maxPrice: maxPriceLimit,
    });
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search products by name, SKU, or description..."
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="pl-9"
        />
      </div>

      <div className="flex gap-2 flex-wrap">
        <Select
          value={filters.category}
          onValueChange={(v) => onFiltersChange({ ...filters, category: v })}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat} className="capitalize">
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.tier}
          onValueChange={(v) => onFiltersChange({ ...filters, tier: v })}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            {tiers.map((tier) => (
              <SelectItem key={tier} value={tier} className="capitalize">
                {tier}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.status}
          onValueChange={(v) => onFiltersChange({ ...filters, status: v })}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Price
              {(filters.minPrice > 0 || (filters.maxPrice > 0 && filters.maxPrice < maxPriceLimit)) && (
                <Badge variant="secondary" className="ml-1 px-1.5">
                  1
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Price Range</Label>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{formatCurrency(priceRange[0])}</span>
                  <span>{formatCurrency(priceRange[1])}</span>
                </div>
                <Slider
                  value={priceRange}
                  min={0}
                  max={maxPriceLimit}
                  step={100}
                  onValueChange={(value) => setPriceRange(value as [number, number])}
                  onValueCommit={(value) =>
                    onFiltersChange({
                      ...filters,
                      minPrice: value[0],
                      maxPrice: value[1],
                    })
                  }
                  className="py-4"
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
            <X className="h-4 w-4" />
            Clear ({activeFiltersCount})
          </Button>
        )}
      </div>
    </div>
  );
}
